import React, {
  createContext,
  RefObject,
  useLayoutEffect,
  useRef,
} from "react";
import styled, { css } from "styled-components";

import { usePageScroll } from "../../hooks/usePageScroll";
import SelectInput from "../inputs/SelectInput";
import { PillButton } from "../text/Pill";

const Styled = styled.div`
  width: 20rem;
`;

const StyledPills = styled.div<{ flexWrap?: "wrap" | "nowrap" }>`
  display: flex;
  flex-wrap: ${({ flexWrap = "wrap" }) => flexWrap};
  gap: 0.5rem;

  padding-bottom: 1rem;

  white-space: nowrap;
  overflow-x: auto;

  // show focus ring on tab target
  ${({ flexWrap = "wrap" }) =>
    flexWrap === "wrap" &&
    css`
      overflow: visible;
    `}
`;

type JumpBarEntry = {
  label: string;
  ref: RefObject<HTMLElement>;
  isSubsection: boolean;
};
type JumpBarContextValue = {
  entries: Record<string, JumpBarEntry>;
  setEntries: React.Dispatch<
    React.SetStateAction<Record<string, JumpBarEntry>>
  >;
};

// Must wrap the JumpBar component, and anyone who plans to use the useJumpBar
// hook. If you would like the JumpBar to update to show the closest jump
// target as you scroll, you need to pass a ref to the scrollable element.
export const JumpBarContext = createContext<JumpBarContextValue>({
  entries: {},
  setEntries: () => {},
});

// Register a component to be included in the nearest JumpBar
export const useJumpBar = (
  { label, ref, isSubsection }: JumpBarEntry,
  disabled?: boolean,
) => {
  const { setEntries } = React.useContext(JumpBarContext);

  React.useEffect(() => {
    if (disabled) return;

    setEntries((entries) => ({
      ...entries,
      [label]: {
        label: label,
        ref: ref,
        isSubsection: isSubsection,
      },
    }));

    return () => {
      setEntries((entries) => {
        const { [label]: _, ...newEntries } = entries;
        return newEntries;
      });
    };
  }, [disabled, setEntries, label, ref, isSubsection]);
};

const getOptions = (entries: Record<string, JumpBarEntry>) =>
  Object.values(entries)
    .filter((entry) => entry.ref.current)
    .sort((a, b) => a.ref.current!.offsetTop - b.ref.current!.offsetTop)
    .map((entry) => ({
      // \xa0 == nbsp;
      label: `${entry.isSubsection ? "\xa0".repeat(4) : ""}${entry.label}`,
      value: entry.label,
      ref: entry.ref,
    }));

const getClosestEntry = (
  entries: Record<string, JumpBarEntry>,
  y: number,
): JumpBarEntry | undefined => {
  // calculate the distance from the top of the document + the scroll offset (used with sticky headers)
  // because offsetTop is only the distance from the top of the nearest offsetParent
  // and we need these numbers to be comparable to our `y` (scroll position)
  const getFullOffsetTop = (entry: JumpBarEntry) => {
    let offsetTop = entry.ref.current!.offsetTop;
    let parent = entry.ref.current!.offsetParent;
    while (parent) {
      if (parent instanceof HTMLElement) {
        offsetTop += parent.offsetTop;
        parent = parent.offsetParent;
      } else {
        break;
      }
    }

    // yes, this is always px. getComputedStyle returns a 'computed (rem -> px, etc) value'
    const scrollMarginTop = +window
      .getComputedStyle(entry.ref.current!)
      .scrollMarginTop.replace("px", "");

    if (scrollMarginTop) {
      offsetTop -= scrollMarginTop;
    }
    return offsetTop;
  };

  const actualElementOffsetTops = Object.values(entries).map((entry) => ({
    entry,
    d: getFullOffsetTop(entry),
  }));

  const sortedEntries = actualElementOffsetTops.sort(
    (a, b) =>
      a.d - b.d ||
      a.entry.ref.current!.compareDocumentPosition(b.entry.ref.current!) &
        Node.DOCUMENT_POSITION_FOLLOWING,
  );

  // find the first entry whose offsetTop ISNT above the top of the screen
  // and then go back one to get the entry we are currently inside of
  // (with a little buffer to account for rounding errors)
  const closestEntry = sortedEntries.findIndex(({ d }) => d > y + 20);

  // if we are above every entry, return undefined
  if (closestEntry === 0) {
    return undefined;
  } else if (closestEntry === -1) {
    // if we are below all entries, return the last one
    return sortedEntries.at(-1)?.entry;
  }

  return sortedEntries.at(closestEntry - 1)?.entry;
};

const IS_CHROME = /Chrome/.test(navigator.userAgent);

const onRequestScroll = (
  entries: Record<string, JumpBarEntry>,
  selectedValue: string,
) => {
  const entry = entries[selectedValue];
  if (entry) {
    entry.ref.current?.scrollIntoView({
      // disabled for chrome until they fix
      // https://bugs.chromium.org/p/chromium/issues/detail?id=833617
      behavior: IS_CHROME ? "auto" : "smooth",
      block: "start",
    });
    entry.ref.current?.focus({ preventScroll: true });
  }
};

/**
 * A react-select component containing all the jump targets in the current
 * document. Useful for navigating long documents.
 *
 * @todo: Deprecate @examspark/app-cms 's JumpBar in favor of this one
 */
export const JumpBar: React.FC<
  React.PropsWithChildren<{
    className?: string;
    menuPosition?: "fixed" | "absolute";
    menuPortalTarget?: HTMLElement | null | undefined;
    disabled?: boolean;
  }>
> = (props) => {
  const { entries } = React.useContext(JumpBarContext);
  const { y } = usePageScroll();
  const options = React.useMemo(() => getOptions(entries), [entries]);
  const closestEntry = getClosestEntry(entries, y);

  return (
    <Styled className={props.className}>
      <SelectInput
        options={options}
        value={closestEntry?.label}
        menuPosition={props.menuPosition ?? "absolute"}
        menuPortalTarget={props.menuPortalTarget}
        // If the target of the jumpbar is hidden, disable it
        isOptionDisabled={(option) => !option.ref?.current?.offsetParent}
        onChange={(value) => {
          onRequestScroll(entries, value?.value ?? "");
        }}
        disabled={props.disabled}
      />
    </Styled>
  );
};

const HighlightedPill = styled(PillButton)<{ isSelected: boolean }>`
  background-color: ${(props) =>
    props.isSelected ? "pink !important " : "hsl(200, 83%, 90%)"};

  border: 1px solid transparent;
  &:hover {
    background-color: hsl(200, 83%, 87%);
  }

  scroll-snap-align: center;
`;

type PilledJumpBarProps = {
  wrap?: "wrap" | "nowrap";
  keepActiveInView?: boolean;
  isTabTarget?: boolean;
};
export const PilledJumpBar = ({
  isTabTarget = true,
  wrap,
  keepActiveInView,
}: PilledJumpBarProps) => {
  const { entries } = React.useContext(JumpBarContext);
  const { y } = usePageScroll();

  const options = React.useMemo(() => getOptions(entries), [entries]);
  const closestEntry = getClosestEntry(entries, y);
  const closestEntryPill = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const closest = closestEntryPill.current;
    if (closest && keepActiveInView) {
      closest.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  }, [closestEntry, keepActiveInView]);

  return (
    <StyledPills flexWrap={wrap} tabIndex={isTabTarget ? undefined : -1}>
      {options.map((option, idx) => (
        <HighlightedPill
          key={idx}
          onClick={() => onRequestScroll(entries, option.value)}
          tabIndex={isTabTarget ? undefined : -1}
          isSelected={option.label === closestEntry?.label}
          ref={option.label === closestEntry?.label ? closestEntryPill : null}
        >
          {/* <HighlightedPill
            isSelected={option.label === closestEntry?.label}
            ref={option.label === closestEntry?.label ? closestEntryPill : null}
          > */}
          {option.label}
        </HighlightedPill>
        // </PillButton>
      ))}
    </StyledPills>
  );
};

export default JumpBar;
